import { useState, useEffect, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import BurritoLogo from "@/components/BurritoLogo";
import RoleAvatar from "@/components/RoleAvatar";
import { Loader2, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function PinModal({ user, onClose, onSuccess }) {
  const { t } = useI18n();
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const { login } = useAuth();

  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  const handleDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);
    setError("");

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 4 digits are entered
    if (value && index === 3) {
      const pin = newDigits.join("");
      if (pin.length === 4) {
        handleSubmit(pin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted.length === 4) {
      const newDigits = pasted.split("");
      setDigits(newDigits);
      inputRefs[3].current?.focus();
      handleSubmit(pasted);
    }
  };

  const handleSubmit = async (pin) => {
    if (pin.length !== 4) return;
    setLoading(true);
    setError("");
    try {
      await login(user.email, pin);
      onSuccess();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        setError(detail || t("login.error.tooMany"));
      } else {
        setError(t("login.error.wrongPin"));
      }
      setDigits(["", "", "", ""]);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="w-full max-w-xs mx-4" onClick={(e) => e.stopPropagation()}>
        <Card className="border shadow-lg">
          <CardContent className="p-6">
            <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-md hover:bg-zinc-100 text-muted-foreground">
              <X className="h-4 w-4" />
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                <RoleAvatar role={user.role} name={user.name} email={user.email} className="w-14 h-14" />
              </div>
              <h3 className="font-heading font-bold text-lg text-foreground">{user.name}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {user.role === "admin" ? t("role.admin") : t("role.user")}
              </p>
            </div>

            <div className="mb-2">
              <p className="text-center text-sm text-muted-foreground mb-4">{t("login.enterPin")}</p>
              <div className="flex justify-center gap-3" onPaste={handlePaste}>
                {/* Fixed 4-slot PIN array — never reorders, index is safe */}
                {digits.map((digit, i) => (
                  <input
                    key={`pin-${i}`}
                    ref={inputRefs[i]}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    disabled={loading}
                    className={`w-12 h-14 text-center text-xl font-bold rounded-md border-2 outline-none transition-all
                      ${error ? "border-destructive bg-red-50" : digit ? "border-primary bg-primary/5" : "border-border"}
                      focus:border-primary focus:ring-2 focus:ring-primary/20
                      disabled:opacity-50`}
                    data-testid={`pin-digit-${i}`}
                  />
                ))}
              </div>
            </div>

            {error && (
              <p className="text-center text-sm text-destructive mt-3 animate-fade-in" data-testid="pin-error">
                {error}
              </p>
            )}

            {loading && (
              <div className="flex items-center justify-center mt-4 gap-2 text-primary">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">{t("login.loading")}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get("/auth/users");
      setUsers(res.data || []);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="h-1 bg-primary" />
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex flex-col items-center">
              <BurritoLogo className="w-20 h-20" />
              <h1 className="font-heading font-bold text-2xl text-foreground mt-4 tracking-tight">
                {t("login.title")}
              </h1>
              <p className="text-muted-foreground mt-1 text-sm">
                {t("login.subtitle")}
              </p>
            </div>
          </div>

          {/* User Select */}
          <div className="mb-4">
            <p className="text-center text-sm text-muted-foreground mb-4">{t("login.selectUser")}</p>
          </div>

          {loadingUsers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid gap-3">
              {users.map((u) => (
                <Card
                  key={u.email}
                  className="cursor-pointer border hover:border-primary/50 hover:shadow-md transition-all active:scale-[0.98]"
                  onClick={() => setSelectedUser(u)}
                  data-testid={`user-card-${u.email.replace(/\s/g, "-")}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <RoleAvatar role={u.role} name={u.name} email={u.email} className="w-12 h-12" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-base text-foreground">{u.name}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {u.role === "admin" ? t("role.admin") : t("role.user")}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <ShieldCheck className="h-5 w-5 text-muted-foreground/40" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <p className="text-center text-xs text-muted-foreground mt-8">
            Tracko Memo &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>

      {/* PIN Modal */}
      {selectedUser && (
        <PinModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSuccess={() => {
            toast.success(t("login.welcomeBack"));
            navigate("/dashboard");
          }}
        />
      )}
    </div>
  );
}
